"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BulkAddShiftsDialog = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_1 = __importDefault(require("@emotion/styled"));
const lodash_1 = require("lodash");
const react_1 = require("react");
const date_1 = require("@bemlo/date");
const constants_1 = require("../../constants");
const elements_1 = require("../../elements");
const DatePicker_1 = require("../../elements/Form/DatePicker");
const DigitInput_1 = require("../../elements/Form/DigitInput");
const hooks_1 = require("../../hooks");
const translate_1 = require("../../translate");
const StyledCell = (0, styled_1.default)(elements_1.Table.Cell) `
  padding: 0;
`;
const StyledHeaderCell = (0, styled_1.default)(elements_1.Table.HeadCell) `
  padding: 0;
  text-align: center;
`;
const IncrementerHover = (0, styled_1.default)(elements_1.Flex) `
  padding: ${constants_1.theme.spacing[1]}px 0;
  button {
    transition: opacity 0.1s ease-in-out;
    opacity: 0;
  }
  justify-content: space-around;
  &:hover {
    button {
      opacity: 1;
    }
  }
`;
const Incrementer = ({ children, onIncrement, incrementDisabled, onDecrement, decrementDisabled, }) => {
    return ((0, jsx_runtime_1.jsxs)(IncrementerHover, { gap: 0.5, children: [(0, jsx_runtime_1.jsx)(elements_1.Clickable, { onClick: onDecrement, disabled: decrementDisabled, styles: { width: 'fit-content' }, children: (0, jsx_runtime_1.jsx)(elements_1.Icon, { color: constants_1.Color.DARKEST_MEDIUM_GREY, size: "sm", name: "MinusCircleIcon" }) }), children, (0, jsx_runtime_1.jsx)(elements_1.Clickable, { onClick: onIncrement, disabled: incrementDisabled, styles: { width: 'fit-content' }, children: (0, jsx_runtime_1.jsx)(elements_1.Icon, { size: "sm", color: constants_1.Color.DARKEST_MEDIUM_GREY, name: "PlusCircleIcon" }) })] }));
};
const DAY_INDICES = Array(7)
    .fill(0)
    .map((_, index) => index);
const Row = ({ variant, values, setValues, }) => {
    const increment = (dayIndex) => {
        const newValues = [...values];
        newValues[dayIndex] += 1;
        setValues(variant.key, newValues);
    };
    const decrement = (dayIndex) => {
        const newValues = [...values];
        newValues[dayIndex] = Math.max(0, newValues[dayIndex] - 1);
        setValues(variant.key, newValues);
    };
    const incrementAll = () => {
        const newValues = values.map((value) => value + 1);
        setValues(variant.key, newValues);
    };
    const decrementAll = () => {
        const newValues = values.map((value) => Math.max(0, value - 1));
        setValues(variant.key, newValues);
    };
    const decrementDisabled = values.every((value) => value <= 0);
    return ((0, jsx_runtime_1.jsxs)(elements_1.Table.Row, { children: [(0, jsx_runtime_1.jsx)(StyledCell, { children: (0, jsx_runtime_1.jsx)(Incrementer, { onIncrement: incrementAll, onDecrement: decrementAll, decrementDisabled: decrementDisabled, children: (0, jsx_runtime_1.jsxs)(elements_1.Text, { size: 6.5, fontWeight: constants_1.theme.font.weight.bold, children: [variant.startTime, " - ", variant.endTime] }) }) }), DAY_INDICES.map((index) => ((0, jsx_runtime_1.jsx)(StyledCell, { children: (0, jsx_runtime_1.jsx)(Incrementer, { onIncrement: () => increment(index), onDecrement: () => decrement(index), decrementDisabled: values[index] === 0, children: (0, jsx_runtime_1.jsx)(elements_1.Badge, { size: "xs", children: values[index] }) }) }, index)))] }));
};
exports.BulkAddShiftsDialog = (0, elements_1.withDialog)(({ shiftVariants, onAddShifts, periodStart, periodEnd, onClose }) => {
    const t = (0, translate_1.useTranslations)();
    const startOfTenderStartOfDay = t.$date.unix(periodStart).startOf('day');
    const endOfTenderEndOfDay = t.$date.unix(periodEnd).endOf('day');
    const [forceShowErrors, setForceShowErrors] = (0, react_1.useState)(false);
    const [start, setStart] = (0, react_1.useState)(startOfTenderStartOfDay.toDate());
    const [periodicity, setPeriodicity] = (0, react_1.useState)(1);
    const [shiftVariantsToAdd, setShiftVariantsToAdd] = (0, react_1.useState)(shiftVariants.reduce((acc, shiftVariant) => ({
        ...acc,
        [shiftVariant.key]: Array(7).fill(0),
    }), {}));
    const updateShiftsToAdd = (shiftVariantId, newValues) => {
        setShiftVariantsToAdd((prev) => ({
            ...prev,
            [shiftVariantId]: newValues,
        }));
    };
    const z = (0, hooks_1.useZod)();
    const schema = z.object({
        periodicity: z.number().int().positive(),
        periodStart: z
            .date()
            .min(startOfTenderStartOfDay.toDate())
            .max(endOfTenderEndOfDay.toDate()),
    });
    const onSubmit = () => {
        const results = schema.safeParse({ periodicity, periodStart: start });
        if (!results.success) {
            setForceShowErrors(true);
            return;
        }
        const periodStartWeekDayJs = t.$date(results.data.periodStart).weekday(0);
        const newShifts = [];
        let currentDay = t.$date(results.data.periodStart);
        while (currentDay.isBefore(endOfTenderEndOfDay)) {
            const dayIndex = currentDay.weekday();
            if (dayIndex === 0) {
                const weekDiff = currentDay.diff(periodStartWeekDayJs, 'week');
                if (weekDiff % results.data.periodicity !== 0) {
                    currentDay = currentDay.add(1, 'week');
                    continue;
                }
            }
            shiftVariants.forEach((shiftVariant) => {
                const value = shiftVariantsToAdd[shiftVariant.key][dayIndex];
                if (value > 0) {
                    for (let i = 0; i < value; i++) {
                        newShifts.push({
                            ...shiftVariant,
                            date: currentDay.format(date_1.ISO_DATE_FORMAT),
                        });
                    }
                }
            });
            currentDay = currentDay.add(1, 'day');
        }
        onAddShifts(newShifts);
        onClose();
    };
    return ((0, jsx_runtime_1.jsxs)(elements_1.CloseButtonDialogBody, { children: [(0, jsx_runtime_1.jsx)(elements_1.Dialog.Title, { children: t.shifts.add_many() }), (0, jsx_runtime_1.jsxs)(elements_1.Flex, { gap: 2, children: [(0, jsx_runtime_1.jsx)(DatePicker_1.DatePicker, { schema: schema.shape.periodStart, reserveErrorSpace: true, forceShowError: forceShowErrors, required: true, minDate: startOfTenderStartOfDay.toDate(), maxDate: endOfTenderEndOfDay.toDate(), value: start, label: t.shifts.add_many_period_start(), onChange: (value) => value && setStart(value) }), (0, jsx_runtime_1.jsx)(DigitInput_1.DigitInput, { schema: schema.shape.periodicity, reserveErrorSpace: true, forceShowError: forceShowErrors, required: true, value: periodicity, onChange: setPeriodicity, label: t.shifts.add_many_periodicity(), placeholder: t.shifts.add_many_periodicity_placeholder() })] }), (0, jsx_runtime_1.jsx)(elements_1.Text, { children: periodicity && start ? (t.shifts.add_many_description(periodicity, t.$date(start).format(date_1.ISO_DATE_FORMAT))) : ((0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: "\u00A0" })) }), (0, jsx_runtime_1.jsx)(elements_1.HorizontalRule, { my: 3 }), (0, jsx_runtime_1.jsxs)(elements_1.Table, { borderless: true, children: [(0, jsx_runtime_1.jsxs)(elements_1.Table.Head, { children: [(0, jsx_runtime_1.jsx)(StyledHeaderCell, {}), DAY_INDICES.map((index) => ((0, jsx_runtime_1.jsx)(StyledHeaderCell, { children: (0, lodash_1.capitalize)(t.$date().weekday(index).format('ddd')) }, index)))] }), (0, jsx_runtime_1.jsx)(elements_1.Table.Body, { children: shiftVariants.map((shiftVariant) => ((0, jsx_runtime_1.jsx)(Row, { variant: shiftVariant, values: shiftVariantsToAdd[shiftVariant.key], setValues: updateShiftsToAdd }, shiftVariant.key))) })] }), (0, jsx_runtime_1.jsx)(elements_1.HorizontalRule, { styles: {
                    marginTop: constants_1.theme.spacing[6.5],
                    marginBottom: constants_1.theme.spacing[3],
                } }), (0, jsx_runtime_1.jsxs)(elements_1.Flex, { gap: 2, children: [(0, jsx_runtime_1.jsx)(elements_1.Button, { size: "sm", onClick: onSubmit, children: t.ui.confirm() }), (0, jsx_runtime_1.jsx)(elements_1.Button, { size: "sm", onClick: onClose, design: "danger-inverted", children: t.ui.cancel() })] })] }));
});
